import { TFunction } from "i18next";
import { label } from "@keycloak/keycloak-ui-shared";
import Grid from '@mui/material/Grid2';
import { ClientRepresentation, DeviceRepresentation, SessionRepresentation } from "../../../api/representations";
import { useMenu } from "../../../providers/MenuProvider";
import { Box, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
    deviceActiveRowGridStyles,
    deviceActiveRowSignOutStyles,
    deviceApplicationsBowStyles,
    deviceBrowserImgBoxStyles,
    deviceBrowserImgStyles,
    deviceBrowserNameStyles,
    deviceChipStyles,
    DeviceImg,
    deviceImgGridStyles,
    deviceImgStyles,
    deviceTooltipStyles,
    passwordButtonStyles,
    SessionHeaderTypography,
    StyledInfoOutlinedIcon
} from "../styles";
import { StyledChip } from "../../../components/styles";
import CheckIcon from "../../../icons/Check";
import Button from "../../../components/button/Button";
import SignOutIcon from "../../../icons/SignOut";
import { formatDate } from "../../../utils/formatDate";
import getBrowserName from "../../../utils/getBrowserName";
import getSoftwareName from "../../../utils/getSoftwareName";


interface ActiveSessionRowProps {
  t: TFunction;
  device: DeviceRepresentation;
  session: SessionRepresentation;
  resourceUrl: string;
  signOutSession: (session: SessionRepresentation, device: DeviceRepresentation) => void;
}

const makeClientsString = (clients: ClientRepresentation[], t: TFunction): string => {
  let clientsString = "";
  clients.forEach((client, index) => {
    let clientName: string;
    if (client.clientName !== "") {
      clientName = label(t, client.clientName);
    } else {
      clientName = client.clientId;
    }
    clientsString += clientName;
    if (clients.length > index + 1) clientsString += ", ";
  });
  return clientsString;
};

export default function ActiveSessionRow({device, session, resourceUrl, t, signOutSession}: ActiveSessionRowProps) {

  const { isOpen } = useMenu();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isDownXl = useMediaQuery(theme.breakpoints.down('xl'));

  return (
    <Grid container size="grow" sx={deviceActiveRowGridStyles} spacing={3}>
      <Grid size={{xs: 'auto'}} sx={deviceImgGridStyles}>
        <DeviceImg sx={deviceImgStyles} src={`${resourceUrl}/images/software/${getSoftwareName(device.os)}.svg`} alt={device.os} />
      </Grid>
      <Grid size="grow">
        <Grid display="flex" justifyContent="space-between">
          <Box sx={deviceBrowserNameStyles}>
            <Box sx={deviceBrowserImgBoxStyles}>
              <DeviceImg sx={deviceBrowserImgStyles} src={`${resourceUrl}/images/browsers/${getBrowserName(session.browser)}.svg`} alt={device.os} />
              <Typography>{session.browser.replace("/", " / ")} / {device.os}</Typography>
            </Box>
            {session.current && (
              <StyledChip sx={deviceChipStyles} icon={<CheckIcon />} size="small" label={t("currentSession")} color="success" variant="outlined" />
            )}
          </Box>

          {!session.current && (
            <Button
              variant="outlined"
              size="small"
              sx={passwordButtonStyles}
              startIcon={<SignOutIcon sx={deviceActiveRowSignOutStyles} />}
              onClick={() => signOutSession(session, device)}
            >
              {t("signOut")}
            </Button>
          )}
        </Grid>
        <Grid container spacing={2.4}>
          <Grid size={{ xs: 12, lg: (isOpen && isDownXl) ? 6 : 4, md: (isOpen && isDownLg) ? 12 : 6 }}>
            <SessionHeaderTypography variant="body2">{t("ipAddress")}</SessionHeaderTypography>
            <Box>{session.ipAddress}</Box>
          </Grid>
          <Grid size={{ xs: 12, lg: (isOpen && isDownXl) ? 6 : 4, md: (isOpen && isDownLg) ? 12 : 6 }}>
            <SessionHeaderTypography variant="body2">{t("lastAccessedOn")}</SessionHeaderTypography>
            <Box>{formatDate(new Date(session.lastAccess * 1000))}</Box>
          </Grid>
          <Grid size={{ xs: 12, lg: (isOpen && isDownXl) ? 6 : 4, md: (isOpen && isDownLg) ? 12 : 6 }}>
            <Box sx={deviceApplicationsBowStyles}>
              <SessionHeaderTypography variant="body2">
                {t("applications")}
              </SessionHeaderTypography>
              <Tooltip placement="top" title={t('signedInApplications')}>
                <StyledInfoOutlinedIcon sx={deviceTooltipStyles} />
              </Tooltip>
            </Box>
            <Box>{makeClientsString(session.clients, t)}</Box>
          </Grid>
          <Grid size={{ xs: 12, lg: (isOpen && isDownXl) ? 6 : 4, md: (isOpen && isDownLg) ? 12 : 6 }}>
            <SessionHeaderTypography variant="body2"> {t("started")}</SessionHeaderTypography>
            <Box>{formatDate(new Date(session.started * 1000))}</Box>
          </Grid>
          <Grid size={{ xs: 12, lg: (isOpen && isDownXl) ? 6 : 4, md: (isOpen && isDownLg) ? 12 : 6 }}>
            <SessionHeaderTypography variant="body2">{t("expires")}</SessionHeaderTypography>
            <Box>{formatDate(new Date(session.expires * 1000))}</Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
